import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useIsAdmin } from '../hooks';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Send, PartyPopper, Cake } from 'lucide-react';

const SlackTesting: React.FC = () => {
  const { t } = useTranslation('slack');
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [anniversaryYears, setAnniversaryYears] = useState('1');

  useEffect(() => {
    // Refresh user data when component mounts to ensure we have latest photoURL
    refreshUser();
  }, []);

  const { isAdmin, isLoading: adminLoading } = useIsAdmin();

  useEffect(() => {
    if (!adminLoading && !isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, adminLoading, navigate]);

  const handleTestBirthday = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await api.post('/slack/test/birthday', {
        userName: user.name,
        userId: user.id,
        userPhotoUrl: user.profilePictureUrl || user.photoURL,
      });

      setSuccess(t('birthday.success'));
    } catch (err: any) {
      setError(err.response?.data?.message || t('birthday.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleTestAnniversary = async () => {
    if (!user) return;

    const years = parseInt(anniversaryYears);
    if (isNaN(years) || years < 1) {
      setError(t('anniversary.error.invalidYears'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await api.post('/slack/test/anniversary', {
        userName: user.name,
        years: years,
        userId: user.id,
        userPhotoUrl: user.profilePictureUrl || user.photoURL,
      });

      setSuccess(t('anniversary.successWithYears', { count: years }));
    } catch (err: any) {
      setError(err.response?.data?.message || t('anniversary.error'));
    } finally {
      setLoading(false);
    }
  };

  if (adminLoading || !isAdmin) {
    return null;
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <h1 className="text-3xl font-bold mb-6">{t('title')}</h1>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-4 border-green-500 bg-green-50">
            <AlertDescription className="text-green-700">{success}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          {/* Birthday Test Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
                  <Cake className="h-6 w-6 text-pink-600" />
                </div>
                <div>
                  <CardTitle>{t('birthday.title')}</CardTitle>
                  <CardDescription>
                    {t('birthday.description')}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('user')}</Label>
                <div className="p-3 bg-almost-black/5 rounded-md">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-almost-black/60">{user?.email}</p>
                </div>
              </div>
              <Button
                onClick={handleTestBirthday}
                disabled={loading}
                className="w-full gap-2"
              >
                <Send className="h-4 w-4" />
                {loading ? t('sending') : t('birthday.button')}
              </Button>
              <p className="text-xs text-almost-black/60">
                {t('birthday.hint')}
              </p>
            </CardContent>
          </Card>

          {/* Anniversary Test Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <PartyPopper className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <CardTitle>{t('anniversary.title')}</CardTitle>
                  <CardDescription>
                    {t('anniversary.description')}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('user')}</Label>
                <div className="p-3 bg-almost-black/5 rounded-md">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-almost-black/60">{user?.email}</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="years">{t('anniversary.yearsOfService')}</Label>
                <Input
                  id="years"
                  type="number"
                  min="1"
                  value={anniversaryYears}
                  onChange={(e) => setAnniversaryYears(e.target.value)}
                  placeholder={t('anniversary.yearsPlaceholder')}
                />
              </div>
              <Button
                onClick={handleTestAnniversary}
                disabled={loading}
                className="w-full gap-2"
              >
                <Send className="h-4 w-4" />
                {loading ? t('sending') : t('anniversary.button')}
              </Button>
              <p className="text-xs text-almost-black/60">
                {t('anniversary.hint')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Information Card */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t('about.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <h4 className="font-medium mb-1">{t('about.automatedNotifications.title')}</h4>
              <p className="text-sm text-almost-black/60">
                {t('about.automatedNotifications.description')}
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-1">{t('about.configuration.title')}</h4>
              <p className="text-sm text-almost-black/60">
                {t('about.configuration.description')}
              </p>
              <ul className="list-disc list-inside text-sm text-almost-black/60 mt-2 space-y-1">
                <li><code className="bg-almost-black/10 px-1 rounded">SLACK_BOT_TOKEN</code> - {t('about.configuration.botToken')}</li>
                <li><code className="bg-almost-black/10 px-1 rounded">SLACK_BIRTHDAY_CHANNEL</code> - {t('about.configuration.birthdayChannel')}</li>
                <li><code className="bg-almost-black/10 px-1 rounded">SLACK_ANNIVERSARY_CHANNEL</code> - {t('about.configuration.anniversaryChannel')}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-1">{t('about.testing.title')}</h4>
              <p className="text-sm text-almost-black/60">
                {t('about.testing.description')}
              </p>
            </div>
          </CardContent>
        </Card>
    </div>
  );
};

export default SlackTesting;
